import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import GemstoneCard from "../components/GemstoneCard";
import Seo from "../components/Seo";
import { collectionPageSchema, breadcrumbSchema } from "../lib/schema";

export const BirthstoneDetail = () => {
  const { slug } = useParams();
  const [b, setB] = useState(null);

  useEffect(() => { api.get(`/birthstones/${slug}`).then(({ data }) => setB(data)); }, [slug]);
  if (!b) return <div className="pt-40 text-center text-white/40">Loading…</div>;

  const monthName = new Date(2024, b.month - 1).toLocaleString("en", { month: "long" });

  return (
    <div data-testid={`birthstone-${slug}`} className="pt-28">
      <Seo
        title={`${monthName} Birthstone · ${b.name} · Meaning, Color & Investment`}
        description={`${b.name} — the birthstone of ${monthName}. ${b.tagline}. ${(b.meaning || "").slice(0, 110)}`}
        path={`/birthstone/${slug}`}
        image={b.image_url}
        jsonLd={[
          collectionPageSchema({
            name: `${monthName} Birthstone — ${b.name}`,
            description: b.tagline || b.meaning || "",
            path: `/birthstone/${slug}`,
            items: b.available_inventory || [],
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Find Your Stone", url: "/find-your-stone" },
            { name: `${monthName} · ${b.name}` },
          ]),
        ]}
      />
      <section className="relative h-[60vh] overflow-hidden mb-20">
        <img src={b.image_url} alt={b.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        <div className="relative z-10 max-w-[1600px] mx-auto h-full px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-16">
          <p className="font-accent text-[11px] tracking-[0.4em] uppercase text-gold mb-5">
            {new Date(2024, b.month - 1).toLocaleString("en", { month: "long" })} · Birthstone
          </p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95]">{b.name}</h1>
          <p className="mt-6 text-ivory text-2xl italic font-heading max-w-2xl">{b.tagline}</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 mb-24 space-y-8 text-white/80 leading-relaxed text-lg font-light">
        <p>{b.meaning}</p>
        <div className="gold-line w-24"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
          <Block title="Color" body={b.color} />
          <Block title="Rarity" body={b.rarity} />
          <Block title="Investment Profile" body={b.investment} />
          <Block title="Historical Use" body={b.history} />
        </div>
        <div>
          <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">Emotional Benefits</p>
          <ul className="space-y-2">
            {b.emotional_benefits?.map((x) => <li key={x} className="text-white/75">— {x}</li>)}
          </ul>
        </div>
        <div>
          <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">Recommended Styles</p>
          <ul className="space-y-2">
            {b.styles?.map((x) => <li key={x} className="text-white/75">— {x}</li>)}
          </ul>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-32">
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Available in our Collection</p>
        <h2 className="font-heading font-light text-3xl md:text-5xl mb-12">Stones aligned with {b.name}.</h2>
        {b.available_inventory?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {b.available_inventory.map((g) => <GemstoneCard key={g.id} gem={g} />)}
          </div>
        ) : (
          <div className="text-white/50">
            None currently in inventory. <Link to="/concierge" className="text-gold hover:text-ivory">Request via concierge.</Link>
          </div>
        )}
      </section>
    </div>
  );
};

export const ZodiacDetail = () => {
  const { slug } = useParams();
  const [z, setZ] = useState(null);

  useEffect(() => { api.get(`/zodiacs/${slug}`).then(({ data }) => setZ(data)); }, [slug]);
  if (!z) return <div className="pt-40 text-center text-white/40">Loading…</div>;

  return (
    <div data-testid={`zodiac-${slug}`} className="pt-28">
      <Seo
        title={`${z.name} Gemstones · Lucky Stones, Traits & Inventory`}
        description={`Lucky gemstones for ${z.name} (${z.dates}). ${(z.narrative || "").slice(0, 130)}`}
        path={`/zodiac/${slug}`}
        image={z.image_url}
        jsonLd={[
          collectionPageSchema({
            name: `${z.name} — Lucky Gemstones`,
            description: z.narrative || `Curated gemstones aligned with the ${z.name} sign.`,
            path: `/zodiac/${slug}`,
            items: z.available_inventory || [],
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Find Your Stone", url: "/find-your-stone" },
            { name: z.name },
          ]),
        ]}
      />
      <section className="relative h-[60vh] overflow-hidden mb-20">
        <img src={z.image_url} alt={z.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        <div className="relative z-10 max-w-[1600px] mx-auto h-full px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-16">
          <p className="font-accent text-[11px] tracking-[0.4em] uppercase text-gold mb-5">
            {z.dates} · {z.element} · Ruled by {z.ruler}
          </p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95]">{z.name}</h1>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 mb-24 space-y-8 text-white/80 leading-relaxed text-lg font-light">
        <p className="text-2xl text-ivory font-heading italic leading-snug">{z.narrative}</p>
        <div className="gold-line w-24"></div>
        <div>
          <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">Personality Traits</p>
          <div className="flex flex-wrap gap-2">
            {z.traits?.map((t) => <span key={t} className="border border-white/20 px-4 py-2 font-accent text-[11px] tracking-[0.18em] uppercase text-white/70">{t}</span>)}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
          <Block title="Color Psychology" body={z.color} />
          <Block title="Recommended Stones" body={z.stones?.join(" · ") || "—"} />
        </div>
        <div>
          <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">Lucky Stone Ranking</p>
          <ol className="space-y-2 list-decimal list-inside">
            {z.lucky_ranking?.map((s) => <li key={s} className="text-white/75">{s}</li>)}
          </ol>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-32">
        <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">From the Inventory</p>
        <h2 className="font-heading font-light text-3xl md:text-5xl mb-12">Stones aligned with {z.name}.</h2>
        {z.available_inventory?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {z.available_inventory.map((g) => <GemstoneCard key={g.id} gem={g} />)}
          </div>
        ) : (
          <div className="text-white/50">No matching pieces currently in inventory.</div>
        )}
      </section>
    </div>
  );
};

const Block = ({ title, body }) => (
  <div>
    <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">{title}</p>
    <p className="text-white/75 leading-relaxed">{body}</p>
  </div>
);
